import {Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {OnboardingTask} from '@src/app/core/models/onboarding-guide.model';
import {OnboardingNavigationService} from '@src/app/core/services/onboarding-navigation.service';
import {OnboardingGuideService} from '@src/app/core/services/onboarding-guide.service';
import {OnboardingGuideComponent} from './components/onboarding-guide/onboarding-guide.component';
import {MatDialog}                               from '@angular/material/dialog';
import {MatSidenav}                              from '@angular/material/sidenav';
import {MatSnackBar}                             from '@angular/material/snack-bar';
import {ActivatedRoute, NavigationEnd, Router}   from '@angular/router';
import {SettingsService} from '@src/app/core/services/settings.service';
import {ExperienceSettingsComponent} from '@src/app/shared/components/experience-settings/experience-settings.component';
import {PurchaseCreditsComponent}                from '@src/app/admin/settings/components/purchase-credits/purchase-credits.component';
import {ImpersonateDialogComponent}              from '@src/app/admin/users/components/impersonate-dialog/impersonate-dialog.component';
import {CreditsService}                          from '@src/app/admin/settings/services/credits.service';
import {UserPreferencesComponent}                from '@src/app/admin/users/components/user-prefereces/user-preferences.component';
import {UsersService}                            from '@src/app/admin/users/services/users.service';
import {UserAccessMode} from '@src/app/admin/users/models/user.model';
import {AuthService}                             from '@src/app/core/services/auth.service';
import {BillingPolicyService} from '@src/app/core/services/billing-policy.service';
import {PaymentService}                          from '@src/app/core/services/payment.service';
import {FeatureFlagService}                      from '@src/app/core/services/feature-flag.service';
import {GlobalService}                           from '@src/app/core/services/global.service';
import {ImpersonationService}                    from '@src/app/core/services/impersonation.service';
import {MaintenanceSummaryService}               from '@src/app/core/services/maintenance-summary.service';
import {PermissionsService}                      from '@src/app/core/services/permissions.service';
import {MarketingConsentModalComponent}          from '@src/app/shared/components/marketing-consent-modal/marketing-consent-modal.component';
import {PusherService}                           from '@src/app/shared/services/pusher.service';
import {ThemeService}                            from '@src/app/shared/services/theme.service';
import {ApiService}                              from '@src/app/shared/services/api.service';
import {WalkthroughService}                      from '@src/app/shared/services/walkthrough/walkthrough.service';
import {environment}                             from '@src/environments/environment';
import {DeviceDetectorService}                   from 'ngx-device-detector';
import {Subscription, timeout, timer}            from 'rxjs';
import Swal                                      from 'sweetalert2';

interface NavigationLink {
    group: 'work' | 'library' | 'organisation';
    name: string;
    url: string;
    icon: string;
    permission?: string | string[];
    featureFlag?: string;
    badge?: number;
}

interface NavigationGroup {
    key: 'work' | 'library' | 'organisation';
    label: string;
    links: NavigationLink[];
}

@Component({
    standalone: false,
    selector: 'app-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit, OnDestroy
{
    public get canUseMobileApp(): boolean {
        return this.authService.user?.access_mode === UserAccessMode.App || this.authService.user?.access_mode === UserAccessMode.Both;
    }

    @ViewChild('sidenav', {static: false}) sideNav: MatSidenav;
    @ViewChild(OnboardingGuideComponent) onboardingGuide: OnboardingGuideComponent;

    public closeMobileNavigation(): void {
        if (this.sideNav.mode === 'push') this.sideNav.close();
    }

    public async openOnboardingGuide(): Promise<void> {
        if (this.sideNav.mode === 'push') await this.sideNav.close();
        // Let Help finish restoring its trigger before moving focus into the guide.
        await Promise.resolve();
        this.onboardingGuide.open();
    }

    public async showTaskNavigation(task: OnboardingTask): Promise<void> {
        const guide = this.onboardingGuide.guide;
        if (!guide) return;
        this.onboardingGuide.minimise(false);
        await this.walkthroughService.stop();
        if (this.sideNav.mode === 'push') await this.sideNav.close();
        this.onboardingNavigation.start(task, guide);
    }

    public onOnboardingOpened(): void {
        this.onboardingNavigation.stop(false);
        void this.walkthroughService.stop();
    }

    public async replayPageGuide(): Promise<void> {
        this.onboardingNavigation.stop(false);
        this.onboardingGuide.minimise(false);
        if (this.sideNav.mode === 'push') await this.sideNav.close();
        await Promise.resolve();
        await this.walkthroughService.replayPage();
    }
    public currentTheme: string = '';
    public title: string = '';
    public pageTitle: string = '';
    public links: NavigationLink[] = [
        {group: 'work', name: 'Dashboard', url: 'app', icon: 'home'},
        {group: 'work', name: 'Sites', url: 'app/sites', icon: 'room', permission: 'view_sites'},
        {group: 'work', name: 'Maintenance', url: 'app/maintenance', icon: 'event_repeat', permission: 'view_maintenance', featureFlag: 'maintenance_reminders'},
        {group: 'work', name: 'Reports', url: 'app/reports', icon: 'bar_chart', permission: ['view_reports', 'view_visit_reports']},
        {group: 'work', name: 'Timesheets', url: 'app/timesheets', icon: 'history', permission: 'view_timesheets', featureFlag: 'timesheets'},
        {group: 'library', name: 'Forms', url: 'app/forms', icon: 'build', permission: 'view_forms', featureFlag: 'form_builder'},
        {group: 'library', name: 'Defects', url: 'app/defects', icon: 'warning', permission: 'view_defects'},
        {group: 'library', name: 'Products', url: 'app/products', icon: 'category', permission: 'view_products'},
        {group: 'library', name: 'Manufacturers', url: 'app/manufacturers', icon: 'precision_manufacturing', permission: 'view_manufacturers'},
        {group: 'organisation', name: 'Clients', url: 'app/customers', icon: 'business', permission: 'view_customers'},
        {group: 'organisation', name: 'Users', url: 'app/users', icon: 'people', permission: 'view_users'},
        {group: 'organisation', name: 'Settings', url: 'app/settings/0', icon: 'settings', permission: ['view_settings', 'manage_master_data', 'add_forms', 'edit_forms']},
    ];

    public readonly navigationGroups: Omit<NavigationGroup, 'links'>[] = [
        {key: 'work', label: 'Work'},
        {key: 'library', label: 'Library'},
        {key: 'organisation', label: 'Organisation'},
    ];
    public expandedNavigation = {library: false, organisation: false};

    public get visibleNavigationGroups(): NavigationGroup[] {
        return this.navigationGroups.map(group => ({...group, links: this.links.filter(link => link.group === group.key && this.isNavigationLinkVisible(link))})).filter(group => group.links.length > 0);
    }

    public isNavigationLinkVisible(link: NavigationLink): boolean {
        const permitted = !link.permission || (Array.isArray(link.permission) ? link.permission : [link.permission]).some(permission => this.permissionsService.can(permission));
        return permitted && (!link.featureFlag || this.featureFlags[link.featureFlag]);
    }

    public isNavigationLinkActive(link: NavigationLink): boolean {
        const path = this.router.url.split(/[?#]/)[0];
        const root = '/' + link.url.split('/').slice(0, 2).join('/');
        return path === root || (root !== '/app' && path.startsWith(root + '/'));
    }

    public isNavigationGroupActive(group: NavigationGroup): boolean {
        return group.links.some(link => this.isNavigationLinkActive(link));
    }

    public setNavigationExpanded(key: 'library' | 'organisation', event: Event): void {
        this.expandedNavigation[key] = (event.target as HTMLDetailsElement).open;
    }

    private openActiveNavigationGroup(): void {
        for (const group of this.visibleNavigationGroups) {
            if (group.key !== 'work' && this.isNavigationGroupActive(group)) this.expandedNavigation[group.key] = true;
        }
    }

    public featureFlags: Record<string, boolean> = {
        form_builder: false,
        maintenance_reminders: false,
        timesheets: false,
    };
    public usesCredits = true;
    public effectivePlan: string | null = null;
    public activeTestPlan: string | null = null;
    public switchingTestPlan = false;
    public readonly testPlans = [
        {value: 'payg', label: 'PAYG'}, {value: 'pro', label: 'Pro'},
        {value: 'grow', label: 'Grow'}, {value: 'expert', label: 'Expert'},
        {value: 'grandfathered_payg', label: 'Grandfathered PAYG'},
    ];
    private readonly billingPolicyService = inject(BillingPolicyService);
    private readonly settings = inject(SettingsService);
    private readonly onboarding = inject(OnboardingGuideService);
    public readonly onboardingNavigation = inject(OnboardingNavigationService);
    private setupDialogOpen = false;
    private featureFlagSubscriptions = new Subscription();
    private maintenanceSummarySubscription?: Subscription;
    private marketingConsentPromptTimeoutId: number | null = null;

    public constructor(
        public dialog: MatDialog,
        public usersService: UsersService,
        public authService: AuthService,
        public impersonationService: ImpersonationService,
        public pusherService: PusherService,
        public walkthroughService: WalkthroughService,
        public themeService: ThemeService,
        public permissionsService: PermissionsService,
        private router: Router,
        private featureFlagService: FeatureFlagService,
        private globalService: GlobalService,
        private creditsService: CreditsService,
        private activatedRoute: ActivatedRoute,
        private snackBar: MatSnackBar,
        private deviceService: DeviceDetectorService,
        private apiService: ApiService,
        private maintenanceSummaryService: MaintenanceSummaryService,
        private paymentService: PaymentService,
    )
    {
        this.title = environment.title;
        this.pageTitle = this.globalService.title

        void this.walkthroughService.initialize();
        this.featureFlagService.initialize(this.usersService.user, this.authService.tenant).then(() => {
            ['form_builder', 'maintenance_reminders', 'site_grants', 'timesheets'].forEach(flag => {
                this.featureFlagSubscriptions.add(this.featureFlagService.getFlag(flag).subscribe((value) => {
                    this.featureFlags[flag] = Boolean(value);
                    this.openActiveNavigationGroup();
                    if (flag === 'maintenance_reminders' && Boolean(value)) this.loadMaintenanceSummary();
                    if (!Boolean(value)) this.leaveRevokedFeaturePage(flag);
                }));
            });
        }).catch(() => {
            this.featureFlags.form_builder = false;
            this.featureFlags.maintenance_reminders = false;
        });

        this.activatedRoute.queryParams.subscribe(params => {
            if (params?.session_id) {
                this.paymentService.checkSession(params.session_id).subscribe({
                    next: async result => {
                        if (result?.state === 'succeeded' && result?.fulfilled === true) {
                            await this.billingPolicyService.initialize(this.authService.tenant?.id, true);
                            this.creditsService.getCreditsBalance().subscribe({
                                next: response => this.authService.credits = response.data.balance,
                                error: () => undefined,
                            });
                        }

                        if (result?.state === 'succeeded' || result?.state === 'pending') {
                            this.snackBar.open('Thanks — your payment is being processed. Your account will update automatically.', 'Ok', {duration: 6000});
                        } else {
                            this.snackBar.open('The payment was not completed. No changes have been made.', 'Ok', {duration: 6000});
                        }
                        this.clearBillingQuery(params);
                    },
                    error: () => {
                        this.snackBar.open('Thanks — your payment is being processed. Your account will update automatically.', 'Ok', {duration: 6000});
                        this.clearBillingQuery(params);
                    },
                });
            }
        });

        if (this.deviceService.isMobile() || this.deviceService.isTablet()) {
            Swal.fire({
                title: 'For the best expereince please use a laptop or desktop device',
                icon: 'warning',
                showCancelButton: false,
                confirmButtonText: 'Ok'
            });
        }
    }

    public ngOnInit(): void
    {
        this.openActiveNavigationGroup();
        this.featureFlagSubscriptions.add(this.settings.refreshPreferences().subscribe({
            next: () => this.showExperienceSetup(), error: () => this.showExperienceSetup(),
        }));
        this.featureFlagSubscriptions.add(this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) { this.openActiveNavigationGroup(); this.showExperienceSetup(); }
        }));
        this.themeService.theme.subscribe(theme => {
            this.currentTheme = theme;
        });

        this.scheduleMarketingConsentPrompt();
        this.maintenanceSummarySubscription = this.maintenanceSummaryService.refreshRequested$.subscribe(() => {
            if (this.featureFlags.maintenance_reminders) this.loadMaintenanceSummary();
        });

        this.featureFlagSubscriptions.add(timer(0, 60000).subscribe(() => this.refreshFeatureFlagContext()));
        this.featureFlagSubscriptions.add(this.billingPolicyService.policy$.subscribe(policy => {
            this.effectivePlan = policy?.plan ?? null;
            this.activeTestPlan = policy?.test_plan ?? this.billingPolicyService.testPlanHeader ?? null;
            this.usesCredits = this.billingPolicyService.usesCredits();
        }));

        void this.billingPolicyService.initialize(this.authService.tenant?.id);

        this.creditsService.getCreditsBalance().subscribe(
            response => this.authService.credits = response.data.balance,
            error => this.creditsService.handleError(error)
        );

        const channel = this.pusherService.listen(`credits.${this.authService.tenant.id}`);
        channel.bind('balance.updated', (data) => {
            this.authService.credits = data.balance;
        });

        const currentUrl = this.router.url;

        if (currentUrl === '/app' || currentUrl === '/app/') {
            this.walkthroughService.start('dashboard', false);
        }
    }

    public logout(): void
    {
        this.authService.logout();
        this.router.navigate(['/login']).then(r => r);
    }

    private loadMaintenanceSummary(): void
    {
        if (!this.permissionsService.can('view_maintenance')) {
            return;
        }

        this.apiService.get('dashboard/maintenance-due', {window_days: 30, per_page: 1}).subscribe({
            next: response => {
                const maintenanceLink = this.links.find(link => link.url === 'app/maintenance');
                if (maintenanceLink) {
                    maintenanceLink.badge = Number(response?.meta?.total ?? 0);
                }
            },
            error: () => undefined,
        });
    }

    private leaveRevokedFeaturePage(flag: string): void
    {
        const route: string | undefined = ({
            form_builder: '/app/forms',
            maintenance_reminders: '/app/maintenance',
            site_grants: '/app/sites/grants',
            timesheets: '/app/timesheets',
        } as Record<string, string>)[flag];
        const currentPath = this.router.url.split('?')[0];
        if (!route || (currentPath !== route && !currentPath.startsWith(`${route}/`))) return;

        this.router.navigate(['/app/settings/7']).then();
        this.snackBar.open('This feature is no longer available. Review your plan or ask an administrator.', 'Ok', {duration: 6000});
    }

    private refreshFeatureFlagContext(): void
    {
        this.apiService.get('me').subscribe({
            next: response => {
                const user = {...this.usersService.user, ...response?.user};
                const tenant = response?.tenant;

                if (!tenant?.id || !user?.id) return;

                this.usersService.user = user;
                this.authService.user = user;
                this.authService.tenant = tenant;
                void this.featureFlagService.initialize(user, tenant);
            },
            error: () => undefined,
        });
    }

    public toggleTheme(): void
    {
        this.themeService.toggleTheme();
    }

    public openUserPreferences(): void
    {
        this.dialog.open(UserPreferencesComponent, {
            width: '50%',
            panelClass: 'mobile-width',
            disableClose: true,
        });
    }

    public openImpersonateDialog(): void
    {
        const impersonateDialog = this.dialog.open(ImpersonateDialogComponent, {
            width: '40%',
            panelClass: 'mobile-width',
            disableClose: true,
            data: {
                currentUserId: this.usersService.user?.id ?? null,
            },
        });

        impersonateDialog.afterClosed().subscribe(result => {
            if (!result?.userId) {
                return;
            }

            this.impersonationService.impersonate(result.userId).subscribe({
                next: response => {
                    this.impersonationService.startSession(response);
                    window.location.reload();
                },
                error: error => {
                    this.snackBar.open(this.getImpersonationErrorMessage(error, 'Unable to start impersonation.'), 'Ok', {
                        duration: 5000,
                    });
                },
            });
        });
    }

    public stopImpersonating(): void
    {
        if (!this.impersonationService.isImpersonating) {
            return;
        }

        const impersonationToken = this.authService.accessToken;
        const finishStop = (): void => {
            this.impersonationService.endSession();
            this.router.navigate(['/app']).then(() => window.location.reload());
        };

        this.impersonationService.stop(impersonationToken).pipe(timeout(5000)).subscribe({
            next: () => finishStop(),
            error: error => {
                if (error?.status !== 400) {
                    console.warn('Impersonation stop API call failed:', error?.status, error?.error?.message);
                }
                finishStop();
            },
        });
    }

    // TODO: add sweet alert to confirm loss of any unsaved data when switching tenant
    // TODO: Create a tenant type
    public switchTenant(tenant): void
    {
        this.authService.tenant = tenant;
        this.router.navigate(['/app/sites']);
        window.location.reload();
    }

    public purchaseCredits()
    {
        if (!this.usesCredits) return;
        const purchaseCreditModal = this.dialog.open(PurchaseCreditsComponent, {
            width: '60%',
            panelClass: 'mobile-width',
            disableClose: true,
            data: null,
        });
    }

    public viewBilling(): void
    {
        this.router.navigate(['/app/settings/7']);
    }

    private clearBillingQuery(params: Record<string, unknown>): void
    {
        const newParams = {...params};
        delete newParams['session_id'];
        this.router.navigate([], {queryParams: newParams});
    }

    public openExternalLink(location: string): void
    {
        let tab: Window;

        switch (location) {
            case 'help':
                window.open('https://surveyor-pro.atlassian.net/servicedesk/customer/portal/3/topic/aa51d8bb-7307-4f7a-a43f-170d72bd075e/article/17727690', '_blank', 'noopener,noreferrer');
                break;

            case 'ticket':
                window.open('https://vulcan-systems.atlassian.net/servicedesk/customer/portals', '_blank');
                break;

            case 'email':
                tab = window.open('mailto:support@firesurveypro.co.uk');
                tab.close();
                break;

            case 'phone':
                tab = window.open('tel:01482 425516');
                tab.close();
                break;
        }
    }

    public async resetTours()
    {
        if (await this.walkthroughService.reset()) {
            this.snackBar.open('Walkthroughs reset', 'Ok', {duration: 2000});
        }
    }

    public async switchTestPlan(plan: string | null): Promise<void>
    {
        if (!this.showDeveloperMenu || this.impersonationService.isImpersonating || this.switchingTestPlan) return;
        this.switchingTestPlan = true;
        try {
            await this.billingPolicyService.switchTestPlan(plan);
            await this.router.navigate(['/app']);
        } catch (error: any) {
            this.snackBar.open(error?.error?.message ?? error?.message ?? 'Unable to switch test plan.', 'Close', {duration: 6000});
        } finally {
            this.switchingTestPlan = false;
        }
    }

    public throwTestError(): void
    {
        throw new Error('Sentry Test Error');
    }

    public get showDeveloperMenu(): boolean
    {
        return this.authService.user?.is_developer === true;
    }

    private getImpersonationErrorMessage(error: any, fallback: string): string
    {
        if (error?.status === 403) {
            return error?.error?.message ?? 'You are not allowed to perform this impersonation action.';
        }

        return error?.error?.message ?? fallback;
    }

    public ngOnDestroy(): void
    {
        if (this.marketingConsentPromptTimeoutId !== null) {
            window.clearTimeout(this.marketingConsentPromptTimeoutId);
        }

        this.onboardingNavigation.stop(false);
        this.featureFlagSubscriptions.unsubscribe();
        this.maintenanceSummarySubscription?.unsubscribe();
    }

    private scheduleMarketingConsentPrompt(): void
    {
        if (!this.settings.setupCompleted) { return; }
        if (!this.authService.shouldScheduleMarketingConsentPrompt()) {
            return;
        }

        this.authService.markMarketingConsentPromptScheduled();
        this.marketingConsentPromptTimeoutId = window.setTimeout(() => {
            if (this.authService.hasAnsweredMarketingConsent()) {
                return;
            }

            this.dialog.open(MarketingConsentModalComponent, {
                width: '32rem',
                panelClass: 'mobile-width',
                disableClose: true,
            });
        }, 30000);
    }

    private showExperienceSetup(): void
    {
        if (this.settings.setupCompleted || this.setupDialogOpen || !this.permissionsService.can('edit_settings') || !/^\/app(?:\/sites)?(?:\?|$)/.test(this.router.url)) { return; }
        this.setupDialogOpen = true;
        this.dialog.open(ExperienceSettingsComponent, {
            width: '480px', maxWidth: 'calc(100vw - 24px)', disableClose: true,
        }).afterClosed().subscribe(saved => {
            this.setupDialogOpen = false;
            if (saved) { this.onboarding.refresh(); this.scheduleMarketingConsentPrompt(); }
        });
    }
}
